import {Directive, ElementRef, inject, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {findKey, forEach, get, includes, isArray, isObject, isString, set, split} from 'lodash-es';

import {getValidJSON, runtimeEventManager} from '@wm/core';
import {
  byType,
  getActionFromKey,
  getCanvasElement,
  getUniqueWidgetName,
  MarkupManagerService,
  updateUrlParams
} from '@wms/core';
import {
  AppManagerService,
  AppRef,
  CanvasDomService,
  PageViewsManager,
  WidgetDetailedInfo,
  WidgetPropertiesProvider
} from '@wms/design-container';
import {PageWsService} from "@wms/workspace";
import {CommandController, WmCommandProvider} from "@wms/app";
import {FileService, NotificationService, ServiceFactoryService, TypeFactoryService} from '@wms/services';
import {DynamicComponentRefProviderService} from "../runtime/services/dynamic-component-ref-provider.service";
import {BaseComponent} from '@wms/app/components';
import {PageLayoutsService} from "@wms/page-workspace";
import {BaseVariableService, VARIABLE_CONSTANTS} from '@wms/variables';
import {getEvaluatedData} from "@wms/properties-panel";
import ClickEvent = JQuery.ClickEvent;
import KeyDownEvent = JQuery.KeyDownEvent;

export interface widgetInfo {
  eventType?: string;
  widgetId?: string;
  widgetType?: string;
  widgetDefaults?: string;
  widgetDropEffect?: string;
  templateEl?: JQuery<HTMLElement> | null;
  isFormInDialog?: boolean;
  configOptions?: any;
  prefabName?: string;
  isTrusted?: boolean;
}

export interface DroppedWidgetInfo extends widgetInfo {
  parentId?: string;
  parentName?: string;
  referenceId?: string;
  action?: string;
  preventSelection?: boolean;
  referenceName?: string;
  eventType?: any;
  widgetConfig?: any;
  prefabName?: string;
  referencePos?: string;
  widgetName?: string;
  element?: JQuery<HTMLElement> | null;
  isChildClickDisabled?: boolean,
  activeLayout?: any
}

interface selectedWidgetInfo {
  event: ClickEvent;
  element: JQuery<EventTarget> | null;
  widgetId: string | undefined;
  widgetType: string | undefined;
}

@Directive({
  selector: 'div[wmsCanvasDom]',
  standalone: true,
  providers: [DynamicComponentRefProviderService, PageViewsManager]
})
export class WmsCanvasDomComponent extends BaseComponent implements OnInit, OnDestroy {

  private markupManager: MarkupManagerService;
  private domElement: JQuery<HTMLElement>;
  private activeWidget!: JQuery<HTMLElement> | null;
  private WidgetProperties: WidgetPropertiesProvider;
  private wmCommand: WmCommandProvider;
  private pageWsService: PageWsService;
  private appManager: AppManagerService
  private pageLayoutService: PageLayoutsService;
  private dynamicComponentProvider: DynamicComponentRefProviderService;
  private notificationService: NotificationService;
  private focusWidgetName: string;
  isMobileApplicationType: boolean;
  isNativeMobileApplicationType:boolean
  isCanvasReady: boolean;
  action: string;
  copiedMarkup: any;
  serviceFactory: ServiceFactoryService;
  variableService: BaseVariableService;
  fileService: FileService;
  private registeredWidgetId?: string;
  private typeService: TypeFactoryService;
  private formWidgetsMap: any = {
    'wm-composite-checkbox'    : 'checkbox',
    'wm-composite-checkboxset' : 'checkboxset',
    'wm-composite-currency'    : 'currency',
    'wm-composite-number'      : 'number',
    'wm-composite-date'        : 'date',
    'wm-composite-datetime'    : 'datetime',
    'wm-composite-radioset'    : 'radioset',
    'wm-rating'                : 'rating',
    'wm-composite-select'      : 'select',
    'wm-composite-chips'       : 'chips',
    'wm-composite-slider'      : 'slider',
    'wm-composite-switch'      : 'switch',
    'wm-composite-text'        : 'text',
    'wm-composite-textarea'    : 'textarea',
    'wm-composite-time'        : 'time',
    'wm-composite-toggle'      : 'toggle',
    'wm-iconbutton-toggle': 'iconbutton-toggle',
    'wm-richtexteditor'        : 'richtext',
    'wm-search'                : 'autocomplete',
    'wm-composite-colorpicker' : 'colorpicker',
    'wm-chips'                 : 'chips',
    'wm-custom-widget'         : 'custom-widget'
  };

  constructor(private canvasDomService: CanvasDomService, private appRef: AppRef,private router: Router, private route: ActivatedRoute) {
    super();
    this.markupManager = inject(MarkupManagerService);
    let elRef = inject(ElementRef);
    this.domElement = $(elRef.nativeElement);
    this.WidgetProperties = inject(WidgetPropertiesProvider);
    this.dynamicComponentProvider = inject(DynamicComponentRefProviderService);
    this.wmCommand = inject(WmCommandProvider);
    this.pageWsService = inject(PageWsService);
    this.pageLayoutService = inject(PageLayoutsService);
    this.appManager = inject(AppManagerService);
    this.serviceFactory = inject(ServiceFactoryService);
    this.variableService = inject(BaseVariableService);
    this.fileService = inject(FileService);
    this.typeService = inject(TypeFactoryService);
    this.isMobileApplicationType = this.projectService.isMobileApplicationType();
    this.isNativeMobileApplicationType = this.projectService.isNativeMobileApplicationType();
    this.pageWsService = inject(PageWsService);
    this.notificationService = inject(NotificationService);
    this.registerDestroyListener.add(this.eventManager.widgetSelectionObservable().subscribe((data: any) => {
      if (!data.hasOwnProperty('disableCanvasDomServiceClick')) { // Added this check to prevent duplicate call from selectWidget via emitOnWidgetSelection
        this.selectWidget(data);
      }
    }));
    this.registerDestroyListener.add(this.eventManager.canvasAddObservable().subscribe((data: any) => {
      this.addWidgetProxy(data, !!data.byPassUndoRedo, !!data.preventWidgetSelection);
      // On widget is dropped on canvas, show the layout options if present
      setTimeout(() => {
        $('.wms-template-layout')?.click();
      }, 10);
    }));

    this.registerDestroyListener.add(this.eventManager.applyAutolayoutObservable().subscribe((data: any) => {
      this.wrapWidgetsIntoContainer(data.widgetIds, data.containerInfo);
    }));

    this.registerDestroyListener.add(this.eventManager.canvasDeleteObservable().subscribe((data: any) => {
      this.deleteWidgetProxy(data);
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('canvas-add-widget').subscribe((data:any) => {
      this.addWidgetProxy(data, !!data.byPassUndoRedo, !!data.preventWidgetSelection );
    }));
    this.registerDestroyListener.add(this.eventManager.addWidgetEventAsObservable().subscribe((data:any) => {
      this.addWidgetProxy(data, !!data.byPassUndoRedo, !!data.preventWidgetSelection );
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('canvas-delete-widget').subscribe((data:any) => {
      this.deleteWidgetProxy(data);
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('canvas-move-widget').subscribe((data:any) => {
      this.moveWidgetProxy(data);
    }));
    this.registerDestroyListener.add(this.eventManager.resetActiveCanvasElObservable().subscribe(() => {
      this.activeWidget = null;
      // this.markupManager.resetIdMap();
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('reset-active-canvas-el').subscribe(() => {
      this.activeWidget = null;
    }));
    this.registerDestroyListener.add(this.eventManager.canvasMoveObservable().subscribe((data: any) => {
      this.moveWidgetProxy(data);
    }));

    this.registerDestroyListener.add(this.eventManager.reRenderCanvasContentObservable().subscribe(() => {
      this.registeredWidgetId = '';
      let $focusNode = $('.wm-focus-rect');
      if (!$focusNode.hasClass('ng-hide')) {
        let activeEl = this.markupManager.getActiveCanvasEl();
        if (activeEl?.hasClass('canvas-active-widget')) {
          activeEl?.trigger('click');
        } else {
          let widgetId = activeEl?.attr('widget-id');
          this.setActiveWidget(widgetId);
        }
      }
    }))
    this.registerDestroyListener.add(this.eventManager.pageRenderedObservable().subscribe(() => {
      this.activeWidget = null;
      this.setActiveWidget();
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('set-active-widget').subscribe((data: string) => {
      this.setActiveWidget(data);
    }));
    this.registerDestroyListener.add(this.eventManager.switchPageLayoutObservable().subscribe((data: any) => {
      this.switchPageLayout(data);
    }));

    this.registerDestroyListener.add(runtimeEventManager.on('canvas-switch-layout').subscribe((data: any) => {
      this.wmCommand.execute(new CommandController(this.markupManager), this, 'switchLayout', data);
      this.pageWsService.startAutoSave();
    }));

    this.registerDestroyListener.add(runtimeEventManager.on('prepare-elements').subscribe((template: JQuery<HTMLElement>) => {
      this.prepareElements(template, true);
    }));
    this.registerDestroyListener.add(this.eventManager.keyboardActionObservable().subscribe((data: string) => {
      this.performAction(data);
    }));

    this.registerDestroyListener.add(runtimeEventManager.on('perform-widget-action').subscribe((data: string) => {
      this.performActionBasedOnKey(data);
    }));

    this.eventManager.layoutGridRowAddObservable().subscribe((data: any) => {
      let templateMarkup: any = '<wm-gridrow>',
        gridRows = this.activeWidget!.children('.app-grid-row'),
        lastRow = gridRows.eq(gridRows.length - 1),
        columnWidth,
        columnsInRow,
        eleDef: any,
        j
      eleDef = {
        widgetDefaults: '',
        widgetId: undefined,
        widgetType: 'wm-gridrow'
      };
      /***if row is selected***/
      if (this.activeWidget!.hasClass('app-grid-row')) {
        eleDef.referenceId = this.activeWidget!.attr('widgetid');
        eleDef.referencePos = data.position;
        eleDef.parentId = this.activeWidget!.parent().attr('widgetid');
        //get the children of selected row
        columnsInRow = this.activeWidget!.children('.app-grid-column');
      } else {
        //the grid is selected
        eleDef.parentId = this.activeWidget!.attr('widgetid');
        columnsInRow = lastRow.children('.app-grid-column');
      }

      for (j = 0; j < columnsInRow.length; j += 1) {
        //copy the columnwidth from the last row
        columnWidth = columnsInRow.eq(j).attr('columnwidth');
        if (!columnWidth) {
          columnWidth = Math.floor(12 / columnsInRow.length);
        }
        templateMarkup += '<wm-gridcolumn columnwidth="' + columnWidth + '"></wm-gridcolumn>';
      }
      templateMarkup += '</wm-gridrow>';
      eleDef.templateEl = $(templateMarkup);
      this.addWidgetProxy(eleDef);
    })
    this.eventManager.layoutGridColumnAddObservable().subscribe((data: any) => {
      let eleDef: any = {
        widgetDefaults: '',
        widgetId: undefined,
        widgetType: 'wm-gridcolumn'
      }, templateMarkup, columnWidth: any = 1, columns, $rowEle;
      /***if column is selected***/
      if (this.activeWidget!.hasClass('app-grid-column')) {
        eleDef.referenceId = this.activeWidget!.attr('widgetid');
        eleDef.referencePos = data.position;
        $rowEle = this.activeWidget!.parent();
        eleDef.parentId = $rowEle.attr('widgetid');
        columns = $rowEle.children('.app-grid-column');
        columnWidth = this.getColumnWidth(columns, true);
      } else {
        //the row is selected
        eleDef.parentId = this.activeWidget!.attr('widgetid');
        columns = this.activeWidget!.children('.app-grid-column');
        columnWidth = this.getColumnWidth(columns);
      }
      //if column width is undefined
      if (!columnWidth) {
        return;
      }
      templateMarkup = '<wm-gridcolumn columnwidth="' + columnWidth + '"></wm-gridcolumn>';
      eleDef.templateEl = $(templateMarkup);
      this.addWidgetProxy(eleDef);
    });
    runtimeEventManager.setService('dynamicComponentProvider', this.dynamicComponentProvider);
    runtimeEventManager.off('selectTabPane');
    this.registerDestroyListener.add(runtimeEventManager.on('addWidgetFromTemplate').subscribe((data: any) => {
      let eleDef: any = {
        templateEl: data.templateEl,
        widgetType: data.widgetType
      };
      // this.canvasDomService.getAddPos(eleDef);
      this.canvasDomService.getDefaultAddPos(eleDef);

      this.addWidget(eleDef, true);
    }));
    this.registerDestroyListener.add(runtimeEventManager.on('selectTabPane').subscribe((data) => {
      this.selectWidget(data);
    }));
    runtimeEventManager.off('selectWizardStep');
    this.registerDestroyListener.add(runtimeEventManager.on('selectWizardStep').subscribe((data) => this.selectWidget(data)));

    runtimeEventManager.off('canvas-switch-template');
    this.registerDestroyListener.add(runtimeEventManager.on('canvas-switch-template').subscribe((data: any) => {
      let pageMarkup = this.markupManager.getMarkup();
      let nodeName = '[widgetid=' + data.widgetId + ']';
      let nodeEl = $(data.markup);
      this.wmCommand.execute(new CommandController(this.markupManager), this, 'switchTemplate', { markup: nodeEl, pageName: this.pageWsService.activePageName });
      this.pageWsService.startAutoSave();
    }));

  }

  get markup() {
    return (this.markupManager as any).markup;
  }

  getColumnWidth(columns: any, isColSel?: any) {
    let tolWidth = 0,
      tmpColWidth = 1,
      columnWidth = 0,
      activeColWidth = 0,
      isEqualWidth = false,
      cWidget,
      curCol;

    //Loop through columns to cal total width and set equal width if all columns are of equal width
    forEach(columns, (column, index: number) => {
      let curColWidth = column.widget.columnwidth;
      tolWidth += parseInt(curColWidth, 10);
      if (curColWidth === tmpColWidth || index === 0) {
        tmpColWidth = curColWidth;
        if (index === columns.length - 1) {
          isEqualWidth = true;
        }
      } else {
        isEqualWidth = false;
      }
    });

    if (tolWidth === 12) {
      //If total is 12 and equal width add new column with the width of 12 / (total no of columns)
      if (isEqualWidth) {
        columnWidth = Math.floor(12 / (columns.length + 1));
        //If column width is '0' or < '0' don't add columns
        if (!columnWidth) {
          return false;
        }
        forEach(columns, (column) => {
          curCol = $(column);
          column.widget.columnwidth = columnWidth.toString();
          this.eventManager.emitSetMarkupAttr('set-markup-attr', curCol.attr('widgetid'), { 'columnwidth': columnWidth }, '');
        });
      } else { // If not equal width and active col is present, set width of half of selected column else set half of last column
        cWidget = isColSel ? this.activeWidget! : columns.eq(columns.length - 1);
        activeColWidth = parseInt(cWidget[0].widget.columnwidth, 10);
        if (activeColWidth > 1) { // If selected column width is less than 2 return else do half of selected column width
          columnWidth = Math.floor(activeColWidth / 2);
          cWidget[0].widget.columnwidth = (activeColWidth - columnWidth).toString();
          this.eventManager.emitSetMarkupAttr('set-markup-attr', cWidget.attr('widgetid'), { 'columnwidth': activeColWidth - columnWidth }, '');
        }
      }
    } else { // If total is not 12 set width of column to remaining width
      columnWidth = 12 - tolWidth;
    }

    return columnWidth;
  }

  ngOnInit(): void {
    this.registerCanvasEvents();
    this.canvasDomService.onInit(this.domElement);

    this.route.queryParams.subscribe((params) => {
      if(params && params["f"]) {
        this.focusWidgetName = params["f"] || "";
      }
    });
    this.route.fragment.subscribe(fragment => {
      if (!fragment) return;

      const params = new URLSearchParams(fragment);
      const focusWidgetName = params.get('item');

      if (focusWidgetName) {
        this.focusWidgetName = focusWidgetName;
      }
    });
  }

  prepareMarkup(markupStr: string) {
    /* just to have a known root for the markup.*/
    markupStr = '<div widgetid="markuproot" widgettype="markuproot">' + markupStr + '</div>';

    /* convert the string to object */
    let markup = $(markupStr);
    this.prepareElements(markup);

    /* wm-livelist and wm-login elements will have ngController directive this will result in
     * error:multidir Multiple Directive Resource Contention
     * to resolve this issue,
     * RunMode: remove the ngController directive from the element and add a wrapper with the controller name
     * StudioMode: remove the ngController directive
     */
    let clonedMarkup = markup.clone();
    // remove data-ng-controller attribute from wm-livelist and wm-login widgets
    // clonedMarkup.find('wm-list, wm-login, wm-template').each(function () {
    //   $(this).removeAttr('data-ng-controller ng-controller');
    // });

    this.markupManager.setMarkup(markup);

    /** Emit this event to know that the markup has loaded*/
    return clonedMarkup.html();
  }

  /*
   * Returns the elements in canvas and markup using the id(string) or the reference(dom node) passed to it.
   */
  findElementsByReference(reference: any): { dom: null | JQuery<HTMLElement>, markup: null | JQuery<HTMLElement> } {
    if (!reference) {
      return {
        "dom": null,
        "markup": null
      };
    }

    let id = null;
    if (isString(reference)) {
      id = reference;
    } else {
      id = $(reference).attr("widget-id");
      if (!id) {
        id = $(reference).closest("[widget-id]").attr("widget-id");
      }
    }

    let el = this.domElement?.find("[widget-id=" + id + "]") || null;
    el = (el && el.hasClass("picture-wrapper") ? el.find("[widget-id=" + id + "]") : el);

    return {
      "dom": el,
      "markup": this.markup?.find("[widget-id=" + id + "]") || null
    };
  }

  setActiveWidget(_widgetId?: string) {
    let widgetId = _widgetId ? _widgetId : this.markup?.children().first().attr('widget-id');
    if(!_widgetId && this.focusWidgetName) {
       const ele = this.markup?.find("[name='" + this.focusWidgetName.trim() + "']") || null;
       if(ele && ele.attr("widget-id")) {
         widgetId = ele.attr("widget-id");
       }
    }
    if (widgetId) {
      let obj = {
        eventType: 'SELECT',
        widgetId: widgetId
      };
      this.selectWidget(obj);
      this.switchPropPanel();
    }
  }

  switchPropPanel() {
    const panel = this.route.snapshot.queryParamMap.get("p");
    const panelMap = new Map<string, number>([
      ['properties', 0],
      ['styles', 1],
      ['events', 2],
      ['device', 3],
      ['security', 4]
    ]);
    if(panel && panelMap.has(panel)) {
      this.eventManager.emitSwitchPropPanel(panelMap.get(panel));
    }
  }


  selectWidget(obj: widgetInfo, isCanvasEvent?: boolean, disableCanvasDomServiceClick?: boolean) {
    let childs: { dom: null | JQuery<HTMLElement>, markup: null | JQuery<HTMLElement> } = this.findElementsByReference(obj.widgetId),
      child: JQuery<HTMLElement> | null = childs.dom,
      _scope: any;

    if (obj && this.activeWidget) {
      if (this.activeWidget.attr('widget-id') === child?.attr('widget-id')) {
        return;
      }
      _scope = get(this.activeWidget[0], 'widget');
      if (_scope) {
        set(_scope, 'active', false);
      }
    }
    if (!this.activeWidget) {
      this.eventManager.emitResetActiveCanvasEl('');
    }

    if (isCanvasEvent) {
      this.activeWidget = child;
      _scope = this.activeWidget && get(this.activeWidget[0], 'widget');
      if (_scope) {
        set(_scope, 'active', true);
      }
      obj.eventType = "SELECT";
      obj.widgetId = child?.attr('widget-id') || '';
      this.markupManager.setActiveMarkupEl(childs.markup);
      this.markupManager.setActiveCanvasEl(child);
      this.eventManager.emitOnCanvasUpdate(obj);
      this.eventManager.emitOnWidgetSelection({ disableCanvasDomServiceClick });
      if (this.activeWidget?.attr('widgettype') === 'wm-page') {
        this.dispatchSafeEvent('logActivePage', {
          pageId: this.markupManager.getActivePageName(),
          markup: childs.markup && childs.markup[0]
        });
      }
    } else {
      child?.click();
    }
    if(this.pageWsService.activeTab === "DESIGN" && (this.pageWsService.isWmEventTriggered || obj.isTrusted)) {
      updateUrlParams([{key: "f", value: this.activeWidget?.attr("name")}], this.router, this.route);
      this.pageWsService.isWmEventTriggered = false;
    }
  }

  /* select the parent widget of the active widget when `ESC` key is pressed */
  selectParentWidget() {
    if (!this.activeWidget) {
      return;
    }

    let parentWidgetId = this.findParentWidgetId({ 'widgetId': this.activeWidget.attr('widgetid') });

    if (parentWidgetId) {
      this.selectWidget({ 'widgetId': parentWidgetId }, true);
    }
  }

  /* select the sibling widget based on the arrow key pressed (prev/next), if not present select the parent widget */
  selectSiblingWidget(method: any) {
    if (!this.activeWidget) {
      return;
    }
    let nodes = this.findElementsByReference(this.activeWidget.attr('widgetid')),
      id,
      $w;

    if (nodes.markup) {
      $w = method === 'prev' ? nodes.markup.prev() : nodes.markup.next() ;
      // if there is a prev widget to the active widget select it,
      // else select the parent
      if ($w.length) {
        id = $w.attr('widgetid');
        if (id) {
          this.selectWidget({ 'widgetId': id }, true);
        } else {
          this.selectParentWidget();
        }
      } else {
        this.selectParentWidget();
      }
    }
  }

  swapWidget(action: any, event: Event) {
    event.stopPropagation();
    if(this.canvasDomService.isSelectedWidgetSortable()) {
      return;
    }
    event.preventDefault();
    if (action === 'prev') {
      this.canvasDomService.swapWithPrev();
    } else {
      this.canvasDomService.swapWithNext();
    }
  }

  addWidgetProxy(details: DroppedWidgetInfo, byPassUndoRedo?: any, preventWidgetSelection?: any) {

    if (details.parentName) {
      details.parentId = this.findWidgetIdByName(details.parentName);
    }

    if (details.referenceName) {
      details.referenceId = this.findWidgetIdByName(details.referenceName);
      if (!details.parentId) {
        details.parentId = this.findParentWidgetId({
          "widgetId": details.referenceId
        });
      }
    }

    // if ($P.hasAccess('workspace.heapCode') && typeof heap !== 'undefined') {
    //   heap.track("Widget Added", {"widgetType": details.widgetType});
    // }

    details.preventSelection = preventWidgetSelection;

    if (byPassUndoRedo) {
      this.addWidget(details, true, preventWidgetSelection);
    } else {
      this.wmCommand.execute(new CommandController(), this, 'addWidget', details);
      this.pageWsService.startAutoSave();
    }
  }

  deleteWidgetProxy(details: DroppedWidgetInfo, byPassUndoRedo?: any, triggeredFromCut?: boolean) {
    details = this.getWidgetDetails(details);
    let elementToBeDeleted = this.findElementsByReference(details.widgetId);
    /*Note : Check if the element can be deleted, before registering the event in wmCommand*/
    if (elementToBeDeleted.markup?.attr('candelete') === 'false') {
      const data = {
        type: 'info',
        title: '',
        desc: this.locale["MESSAGE_ERROR_DELETE_WIDGET_NOT_ALLOWED"]
      }
      runtimeEventManager.emit('show-toaster', data);
      return false;
    }
    if (byPassUndoRedo) {
      this.deleteWidget(details, true);
    } else {
      this.wmCommand.execute(new CommandController(), this, 'deleteWidget', details);
      this.pageWsService.startAutoSave();
    }

    // delete the variable for file upload widget
    if (details.widgetType === 'wm-fileupload') {
      let onSelectBinding = details.templateEl?.attr('on-select'),
        variableName;
      if (onSelectBinding?.startsWith('Variables.')) {
        variableName = onSelectBinding.split(".")[1];
        // Variables.deleteDefaultVariable({
        //   name: variableName,
        //   isDefault: true
        // });
      }
    }
    return true;
  }

  moveWidgetProxy(details: DroppedWidgetInfo) {
    this.wmCommand.execute(new CommandController(), this, 'moveWidget', details);
    this.pageWsService.startAutoSave();
  }

  moveWidget(details: DroppedWidgetInfo, isCanvasEvent?: boolean) {
    let childs = this.findElementsByReference(details.widgetId),
      parents = this.findElementsByReference(details.parentId), /* new Parent */
      references = this.findElementsByReference(details.referenceId),
      child = childs.dom,
      parent = parents.dom,
      reference = references.dom,

      /* old parent references required for removing parent list-item element*/
      oldParentContainer = childs.dom?.parent(),
      oldParentMarkup = childs.markup?.parent(),
      hasTransclude;

    if (childs.dom && parents.dom && (childs.dom.find(parents.dom).length > 0 || childs.dom.attr("widgetid") === parents.dom.attr("widgetid"))) {
      return; // parent dropped on its child.
    }

    if (reference) {
      if(details.referencePos === 'inside' && child){
        reference.append(child)
      }else{
      // @ts-ignore
      reference[details.referencePos](child);
      }
    } else {
      hasTransclude = parent?.attr('wmstransclude');
      if (hasTransclude === "" && child) {
        parent?.append(child);
      } else {
        child && parent?.find('[data-droptarget-for=' + parent.attr('widgetid') + ']').append(child);
      }
    }

    /* delete the parent list item if it exists */
    if (this.isListItem(oldParentContainer)) {
      this.deleteListItem(oldParentContainer, oldParentMarkup);
    }

    /*fire the click event on the widget element to trigger actions*/
    if (!details.isChildClickDisabled) {
      child?.click();
    }

    /* markup */
    child = childs.markup;
    parent = parents.markup;
    reference = references.markup;
    if(details.referencePos === 'inside'){
      child && reference?.append(child);
    }else if (reference && details.referencePos) {
      // @ts-ignore
      reference[details.referencePos](child);
    } else {
      child && parent?.append(child);
    }

    if(details.eventType === 'TREE_NODE_MOVED'){
      return;
    }

    if (isCanvasEvent) {
      details.eventType = 'MOVE';
      this.eventManager.emitOnCanvasUpdate(details);
      //Widgets like charts when moved to containers need to re-size
      // TODO
      // $rootScope.$emit('canvas-resize');
    }
  }

  getWidgetDetails(details: DroppedWidgetInfo) {
    if (details.widgetName) {
      details.widgetId = this.findWidgetIdByName(details.widgetName);
    }

    let canvasEl = this.findElementsByReference(details.widgetId).dom,
      prevEl = canvasEl?.prev(),
      parentEl = canvasEl?.parent().closest('[widgetid]'),
      widgetConfig,
      widgetContainer,
      siblingElement,
      parentContainer;


    details.widgetType = canvasEl?.attr('widgettype');
    details.element = canvasEl;
    /*For prefab undo, prefab name is required*/
    if (details.widgetType === "wm-prefab") {
      details.prefabName = canvasEl?.attr('prefabname');
    }

    widgetConfig = details.widgetType ? this.WidgetProperties.getConfig(details.widgetType) : {};
    //check if the widget has parent to be deleted
    if (widgetConfig.parent) {
      //standard widgets which are placed in the parent containers
      widgetContainer = canvasEl?.closest('[widgettype="' + widgetConfig.parent + '"]');
      siblingElement = widgetContainer?.prev().closest('[widgetid]');
      parentContainer = widgetContainer?.parent().closest('[widgetid]');
      details.referenceId = siblingElement?.attr('widgetid');
      details.referencePos = "after";
      details.parentId = parentContainer?.attr('widgetid');
      details.widgetType = widgetContainer?.attr('widgettype');
      details.element = widgetContainer;
      details.widgetId = widgetContainer?.attr('widgetid');
    } else {
      details.referenceId = prevEl?.attr('widgetid');
      details.referencePos = "after";
      details.parentId = parentEl?.attr('widgetid');
    }
    /** We are maintaining the markup, to be used for undo/redo */
    details.templateEl = this.findElementsByReference(details.widgetId).markup;

    return details;
  }

  deleteWidget(obj: DroppedWidgetInfo, isCanvasEvent?: boolean, byPassFocus?: boolean) {
    let childs = this.findElementsByReference(obj.widgetId),
      parentEl = this.findElementsByReference(this.findParentWidgetId({ "widgetId": obj.widgetId })), // Not getting the parent through dom as the parent may be the wrapper due to resizable directive
      parentContainer = childs.dom?.parent(),
      grandParent = parentContainer?.parent(),
      scope,
      childDialog,
      controllerWidgets = ['wm-table', 'wm-liveform', 'wm-list', 'wm-login'],
      controllerWidgetNames = [],
      controllerWidgetArray,
      elementTobeRemoved: string | undefined;
    /* needed to select list element */

    scope = childs.dom && get(childs.dom[0], 'widget');

    if (scope) {
      /* for some un-known reason $destroy is not getting fired when the element is removed.
       * so fire it manually on `this` element and all the widgets inside it. */

      (scope as any).destroy.complete();
      // destroying inner elements in the element.
      forEach(childs.dom?.find('[widgetid]'), (el: any) => {
        let innerScope = get(el, 'widget');
        if (innerScope) {
          (innerScope as any).destroy.complete();
        }
      })
    }

    elementTobeRemoved = childs.dom?.attr('widgetid');

    /* check if widget is within a list item element if so delete the list item too when its empty*/
    if (this.isListItem(parentContainer) && parentEl.dom?.children().length === 0) {
      parentContainer && this.deleteListItem(parentContainer, childs.markup?.parent());
      grandParent?.click();
      elementTobeRemoved = parentContainer?.attr('widgetid');
    }

    /*Trigger click event on the element's parent to make it selectable after the element is removed*/
    if (parentEl.dom && !byPassFocus) {
      setTimeout(() => {
        parentEl.dom?.click();
        parentEl.dom?.find('.wm-focus-rect').remove();

        let widgetRef = childs.dom ? (childs.dom[0] as any).widget : null;

        if (widgetRef && widgetRef.onRemove) {
          widgetRef.onRemove();
        }
        childs.dom?.remove();
        childs.markup?.remove();
        // emit the event to reset the selected view to Page/Partial
        if (childs.dom?.attr('widgettype')?.endsWith('dialog')) {
          // TODO: need to implement wms:delete-dialog
          // domScope.$root.$emit('wms:delete-dialog');
        }

        if (isCanvasEvent) {
          obj.eventType = 'DELETE';
          obj.widgetId = elementTobeRemoved;
          this.eventManager.emitOnCanvasUpdate(obj);
        }
      }, 0, false);
    }
    /*finding the child dialog of the dialogview*/
    childDialog = childs.dom?.find('[widgettype$="dialog"]');
    /*If widgets for which controllers were added are being deleted, emit event in workspace controller to remove corresponding controller from script file*/
    if (obj.widgetType && controllerWidgets.indexOf(obj.widgetType) !== -1) {
      controllerWidgetNames.push(obj.element?.attr('name'));
    } else if (obj.widgetId?.indexOf('wm-view') !== -1 && childDialog && childDialog.length) {
      controllerWidgetNames.push(childDialog.attr("widgetid"));
    } else if (obj.widgetType === 'wm-livetable') {
      controllerWidgetArray = obj.element?.find('.app-grid, .app-liveform');
      forEach(controllerWidgetArray, (element: HTMLElement) => {
        controllerWidgetNames.push($(element).attr('name'));
      });
    }
    if (childDialog && childDialog.length) {
      // TODO: implement wms:delete-dialog
      //domScope.$root.$emit('wms:delete-dialog');
    }
    this.pageWsService.isWmEventTriggered = true;
    this.eventManager.emitOnPostWidgetDelete({ widgetId: childs.dom?.attr('widgetid'), parentWidgetId: parentEl.dom?.attr('widgetid') });
  }

  // Returns the widgetid of the element having the given name.
  findWidgetIdByName(name: string) {
    return this.domElement.find("[name=" + name + "]").attr("widget-id");
  }

  // Find the widgetid of the parent either using widgetname or widgetid
  findParentWidgetId(details: { widgetId?: string, widgetName?: string }) {
    if (details.widgetName) {
      details.widgetId = this.findWidgetIdByName(details.widgetName);
    }

    if (details.widgetId) {
      return this.domElement.find("[widget-id=" + details.widgetId + "]").parent().closest("[widget-id]").attr("widget-id");
    }
    return '';
  }

  getWidgetTemplate(widgetConfig: WidgetDetailedInfo, platformType: string, isFormParent: boolean, isFilterParent: boolean) {
    let iconToggleAttrs = '';
    if(this.formWidgetsMap[widgetConfig.widgetType] === 'iconbutton-toggle'){
          iconToggleAttrs = ` selectedicon="wm-sl-l sl-volume-off" unselectedicon="wm-sl-l sl-volume-full"`;
    }
    //If parent is a form and widget is a form widget, generate form field
    if (isFormParent && this.formWidgetsMap[widgetConfig.widgetType]) {
      return '<wm-form-field displayname="Custom field" widget="' + this.formWidgetsMap[widgetConfig.widgetType] + '" show="true" type="custom" '+iconToggleAttrs+'></wm-form-field>';
    }
    //If parent is a filter and widget is a form widget, generate filter field
    if (isFilterParent && this.formWidgetsMap[widgetConfig.widgetType]) {
      return '<wm-filter-field displayname="Custom field" widget="' + this.formWidgetsMap[widgetConfig.widgetType] + '" show="true" type="custom" '+iconToggleAttrs+'></wm-filter-field>';
    }
    if (widgetConfig.platforms && widgetConfig.platforms[platformType] && widgetConfig.platforms[platformType].template) {
      return widgetConfig.platforms[platformType].template;
    }
    if(this.projectService.isPrism() && widgetConfig.widgetType === "wm-container"){
      return `<wm-container direction='row' alignment='top-left' gap='4' width='fill'></wm-container>`
    }else if(this.projectService.isPrism() && widgetConfig.widgetType === "wm-progress-bar"){
      return `<wm-progress-bar datavalue='30' width="320px"></wm-progress-bar>`
    }
    return widgetConfig.template;
  }
  isDroppedOnList(parentElement: JQuery<HTMLElement>) {
    return parentElement.attr("data-element-type") === "wmList" || parentElement.attr("data-element-type") === "wmNav";
  }

  getListItemType(parentElement: JQuery<HTMLElement>): any {
    if (parentElement.attr("data-element-type") === "wmList") {
      return "list";
    }
    if (parentElement.attr("data-element-type") === "wmNav") {
      return "nav";
    }

  }

  generateListItemWrapper(listItem: any) {
    let widgetType = "wm-" + listItem + "-item",
      widgetId = this.markupManager.getUniqueIdByType(widgetType),
      domContent,
      _markup;
    domContent = $(document.createElement(widgetType));
    domContent.attr({
      "widgettype": widgetType,
      "widgetid": widgetId
    });

    _markup = $("<" + widgetType + "></" + widgetType + ">")
      .attr({
        "widgettype": widgetType,
        "widgetid": widgetId
      });
    return { "dom": domContent, "markup": _markup };
  }

  addWidget(obj: DroppedWidgetInfo, isCanvasEvent: boolean, preventSelectOnAdd?: boolean) {
    /*adding dialog with view*/
    /** obj.templateEl is the markup. If we have the markup(comes in case of undo/redo)
     * then use the markup instead of creating the markup again.
     * For dialog, we should not create the parent view if markup is present
     * Only in form in dialog case there will be templateEl
     * */
    if (obj.widgetType && (obj.widgetType.indexOf('dialog') > -1 || obj.widgetType === 'wm-bottomsheet') && (!obj.templateEl || obj.isFormInDialog)) {
      // this.domScope.$root.$emit('set-active-widget', this.markup.children(0).attr("widgetid"));
      /* dialog view is meant to have the dialog only, widgets should not be dropped on the same,
       * so making the dialog-view non-droppable
       * */
      let markup = this.markupManager.getMarkup();
      let pageRootNode = markup && markup[0].firstChild;
      if (get(pageRootNode, 'getAttribute')) {
        obj.parentId = (pageRootNode as HTMLElement).getAttribute('widgetid') as string;
      }
      this.selectWidget(obj);
      /*Setting referenceId to undefined so that the dialog is added to the view we just added*/
      obj.referenceId = undefined;
    }

    let oldWidgetType: string = obj.widgetType || '',
      widgetType: string = oldWidgetType,
      parentEl: any,
      referenceEl: JQuery<HTMLElement>,
      markupChild,
      templateEl: JQuery<HTMLElement>, // for the canvas
      clonedTemplateEl: JQuery<HTMLElement>, // for the markup
      listItem: { dom: null | JQuery<HTMLElement>, markup: null | JQuery<HTMLElement> },
      isWidgetDroppedOnList,
      // canvasDOM
      parents: { dom: null | JQuery<HTMLElement>, markup: null | JQuery<HTMLElement> } = this.findElementsByReference(obj.parentId),
      references: { dom: null | JQuery<HTMLElement>, markup: null | JQuery<HTMLElement> } = this.findElementsByReference(obj.referenceId),
      widgetConfig = widgetType ? this.WidgetProperties.getConfig(widgetType) : {},
      hasTransclude,
      prefabName,
      widgetName,
      platformType = this.appRef.platformType.toLowerCase(),
      liveWidgetId: any,
      bindAttr,
      bindNode,
      isFormParent: boolean = !!(parents.markup?.closest('[widgettype="wm-liveform"], [widgettype="wm-form"]').length),
      isFilterParent: boolean = isFormParent ? false : !!(parents.markup?.closest('[widgettype="wm-livefilter"]').length),
      $activeWidgetEl;

    /* fetch the template of the widget being added
     *  If the addWidget is called via Paste then get the templateEl from
     *  the object passed else get it from the widgetConfig
     * */

    if (obj.templateEl) {
      templateEl = obj.templateEl;
    } else {
      templateEl = $(this.getWidgetTemplate(widgetConfig, platformType, isFormParent, isFilterParent));
    }

    parentEl = parents.dom as JQuery<HTMLElement>;
    referenceEl = references.dom as JQuery<HTMLElement>;

    /* check if drop is on list element - if so wrap it with a list item element*/
    if (parentEl) {
      isWidgetDroppedOnList = this.isDroppedOnList(parentEl);
      if (isWidgetDroppedOnList) {
        listItem = this.generateListItemWrapper(this.getListItemType(parentEl));
        templateEl = (listItem.dom as JQuery<HTMLElement>).append(templateEl);
      }
    }


    /* template is modified update the widgetConfig and widgetType*/
    widgetType = templateEl.prop('tagName').toLowerCase();

    if (widgetType === 'wm-composite') {
      widgetType = obj.widgetType || '';
    }
    widgetConfig = widgetType ? this.WidgetProperties.getConfig(widgetType) : {};
    obj.widgetType = widgetType;

    if (widgetType === "wm-prefab") {
      prefabName = obj.prefabName;
      if (!prefabName) {
        let defaults = (obj.widgetDefaults ? JSON.parse(obj.widgetDefaults) : {});
        prefabName = defaults.prefabName;
      }
      templateEl.attr("prefabname", prefabName);
    }

    if (widgetType === "wm-custom-widget" || obj.widgetName) {
      widgetName = obj.widgetName;
      if (!widgetName) {
        let defaults = (obj.widgetDefaults ? JSON.parse(obj.widgetDefaults) : {});
        widgetName = defaults.widgetName;
      }
      templateEl.attr("widgetname", widgetName);
    }

    if (widgetType === "wm-linearlayout") {
      if (parentEl && parentEl.closest('.app-linear-layout-item').length > 0) {
        templateEl.removeAttr("spacing");
        templateEl.removeAttr("padding");
      }
    }

    if (widgetType === "wmx-component") {
      templateEl.attr("componentname", (obj as any).componentName);
    }

    this.prepareElements(templateEl, true, obj.action);

    // Add btn-filled class to form action buttons for prism projects
    if (this.projectService.isPrism() && widgetType === 'wm-form') {
      this.addBtnFilledToFormActionButtons(templateEl);
    }

    // Add btn-filled class to dialog action buttons for prism projects
    if (this.projectService.isPrism() && widgetType.includes('dialog')) {
      this.addBtnClassesToDialogButtons(templateEl);
    }

    if (templateEl[0].outerHTML.indexOf('$wm-expr(') > -1) {
      // If we add any attribute on widgets based on the widget property such as widgetId, name
      // Then these attributes are evaluated and replaced with their values here
      this.evaluateElemAttr(templateEl[0]);
    }

    if (this.isMobileApplicationType && widgetType === 'wm-fileupload') {
      templateEl.attr('contenttype', 'image/*');
    }
    if (this.isNativeMobileApplicationType && widgetType === 'wm-fileupload') {
      templateEl.attr('contenttype', 'image/*');
    }

    //If widget is live grid and template is filter get widget id of filter and set dataset on to grid.
    if (includes(['wm-livetable', 'wm-list'], obj.widgetType) &&
      includes(['grid-form-filter', 'list-form-web', 'list-form-device', 'dialog-form'], obj.activeLayout && obj.activeLayout.id)) {
      if (obj.activeLayout.id === 'grid-form-filter') {
        bindAttr  = 'dataset';
        bindNode  = 'result';
      } else {
        bindAttr  = 'formdata';
        bindNode  = 'selecteditem';
      }
      liveWidgetId = templateEl.eq(0).attr('name') || templateEl.eq(0).attr('widgetid')?.split('_')[1];
      templateEl.eq(1).attr(bindAttr, 'bind:Widgets.' + liveWidgetId + '.' + bindNode).attr('dependson', liveWidgetId);
    }

    //If parent element is list and the dropped widget has same dataset then override few things
    if (parentEl?.closest('.app-livelist').length) {
      let listEl = parentEl.closest('.app-livelist'),
        listDataset = 'bind:' + listEl.attr('dataset.bind'),
        activeDataset = templateEl.attr('dataset'),
        variableName = activeDataset && split(activeDataset, '.').length ? split(activeDataset, '.')[1] : false,
        pageScope,
        livelistName = listEl[0].widget.name;
       if(this.projectService.getDetails().type === 'PREFAB'){
           pageScope = parentEl.closest('.app-prefab-container')[0].widget.viewParent
         }else{
         if(this.pageWsService.pageInfo.type === 'PAGE') {
           pageScope = parentEl.closest('.app-page')[0].widget.viewParent
         }
         if(this.pageWsService.pageInfo.type === 'PARTIAL' && this.pageWsService.pageInfo.name === this.pageWsService.activePageName){
           pageScope = parentEl.closest('.app-partial')[0].widget.viewParent
         }
         }

      if (activeDataset && listDataset === activeDataset && variableName && pageScope.Variables[variableName].category === 'wm.LiveVariable') {
        //If its form make the form data as bind:item
        if (obj.widgetType === 'wm-liveform') {
          templateEl.attr('formdata', 'bind:Widgets.' + livelistName + '.currentItem');
        }
      }
    }

    if (obj.widgetDefaults) {
      this.applyWidgetDefaults(templateEl, widgetType, obj.widgetDefaults);
    }

    /*for table widget if the configured layout type is crud-grid, the templateEl will not have wrapper/container, it will be a collection of template elements -
    so each template has to be rendered separately*/
    if (includes(['wm-table'], obj.widgetType) && includes(['crud-grid-form-dialog', 'crud-grid-form', 'crud-grid-form-readonly'], obj.activeLayout && obj.activeLayout.id)) {
      liveWidgetId = templateEl.eq(0).attr('name') || templateEl.eq(0).attr('widget-id')?.split('_')[1];
      templateEl.eq(1).attr('dependson', liveWidgetId);
      this.renderCrudGridTemplates(templateEl, referenceEl, obj, parentEl, preventSelectOnAdd, $activeWidgetEl)
    }
    else {
      this.dynamicComponentProvider.addComponentBasedOnSelector(referenceEl ? referenceEl[0] : null, (templateEl[0].outerHTML).toString(), {}, {
        transpile: true,
        addAfter: obj.referencePos === 'after',
        parentEl: parentEl
      }).then((el: HTMLElement) => {
        setTimeout(() => {
          // Set initial appearance/variant options and values on the widget instance
          this.initializeAppearanceVariant(el);
          if (!preventSelectOnAdd) {
            $activeWidgetEl = this.getWidgetToActivate($(el));
            $activeWidgetEl.click();
          }
        }, 0, false);
      });
    }

    obj.widgetId = templateEl.attr('widget-id');
    clonedTemplateEl = templateEl.clone();

    markupChild = clonedTemplateEl;

    parentEl = parents.markup as JQuery<HTMLElement>;
    referenceEl = references.markup as JQuery<HTMLElement>;

    if (referenceEl) {
      if (obj.referencePos === 'after') {
        referenceEl.after(markupChild);
      } else if (obj.referencePos === 'before') {
        referenceEl.before(markupChild);
      }
    } else if (parentEl) {
      if (obj['widgetType'] === 'wm-wizardstep') {
        const wizardAction = $(parentEl[0]).find('wm-wizardaction');
        wizardAction.length > 0 ? markupChild.insertBefore(wizardAction) : parentEl.append(markupChild);
      } else {
        parentEl.append(markupChild);
      }
    }

    if (widgetType === 'wm-fileupload') {
      (this.serviceFactory.isFileServiceRegistered() as Promise<any>).then((isFileReg: boolean) => {
        if (!isFileReg) {
          // this method will register the file-upload-widget.
          /* register the file-upload-widget & then add the widget to canvas */
          (this.fileService.registerFileService() as Promise<any>).then(() => {
            this.addFileUploadWidgetToCanvas(obj.widgetId);
          }).then(() => {
            this.typeService.getTypes();
          }).catch(() => {
          });
        }
        else {
          this.addFileUploadWidgetToCanvas(obj.widgetId);
        }
      });
    }

    if (isCanvasEvent) {
      obj.eventType = 'ADD';
      obj.widgetConfig = widgetConfig;
      obj.templateEl = clonedTemplateEl;
      this.eventManager.emitOnCanvasUpdate(obj);
    }
    if (includes(['wm-view', 'wm-dialog', 'wm-alertdialog', 'wm-bottomsheet', 'wm-confirmdialog', 'wm-iframedialog', 'wm-pagedialog', 'wm-logindialog'], obj.widgetType)) {
      this.appManager.notify('markup-loaded', true);
      // $rootScope.$emit('wms:reset-views');
      // //Emit add view to select dialog on drag n drop of dialogs
      // $timeout(function() {
      //   $rootScope.$emit('wms:add-dialog');
      // });
    }
    this.pageWsService.isWmEventTriggered = true
  }

  /**
   * Wraps a list of widgets within a new container widget.
   * @param widgetIds An array of widget IDs to be moved into the new container.
   * @param containerInfo An object containing information for the new container, same as the 'obj' in addWidget.
   */
  wrapWidgetsIntoContainer(widgetIds: string[], containerInfo: DroppedWidgetInfo) {
      // Find the insertion point for the new container in both DOM and markup
      const containerParents = this.findElementsByReference(containerInfo.parentId);
      const containerReferences = this.findElementsByReference(containerInfo.referenceId);

      // Get the last widget in the selection
      const lastWidgetId = widgetIds[widgetIds.length - 1];
      const lastWidgetElements = this.findElementsByReference(lastWidgetId);

      if (!lastWidgetElements.dom || !lastWidgetElements.markup) {
          console.error(`Last widget to wrap ('${lastWidgetId}') could not be found.`);
          return;
      }

      // Get stable insertion point from the last widget's position
      const stableParentDom = lastWidgetElements.dom.parent();
      const stableReferenceNodeDom = lastWidgetElements.dom[0].nextSibling as HTMLElement;;

      const stableParentMarkup = lastWidgetElements.markup.parent();
      const stableReferenceNodeMarkup = lastWidgetElements.markup.next();

      if (!containerParents.dom || !containerParents.markup) {
          console.error("Could not find parent element to wrap widgets.");
          return;
      }

      // Gather all widget elements (DOM and markup) to be moved.
      const childWidgets = widgetIds.map(id => {
          const elements = this.findElementsByReference(id);
          if (!elements.dom || !elements.markup) {
              console.warn(`Widget with ID ${id} not found. Skipping.`);
              return null;
          }
          return {
              id: id,
              dom: elements.dom,
              markup: elements.markup,
              // Store old parent for list-item cleanup later
              oldParentContainer: elements.dom.parent(),
              oldParentMarkup: elements.markup.parent()
          };
      }).filter(Boolean); // Filter out any nulls if widgets weren't found

      const widgetConfig = containerInfo.widgetType ? this.WidgetProperties.getConfig(containerInfo.widgetType) : {};

      // 1. Create the container element for the canvas (live DOM)
      let containerCanvasEl = $(this.getWidgetTemplate(widgetConfig, this.appRef.platformType.toLowerCase(), false, false));
      this.prepareElements(containerCanvasEl, true, containerInfo.action);
      if (containerInfo.widgetDefaults) {
          this.applyWidgetDefaults(containerCanvasEl, (containerInfo.widgetType || ""), containerInfo.widgetDefaults);
      }
      // Set the widgetId on our in-memory element so it can be found later
      containerInfo.widgetId = containerCanvasEl.attr('widget-id');

      // 2. Create a clone for the markup
      let containerMarkupEl = containerCanvasEl.clone();

      // Detach children from the live DOM and append them to the in-memory container.
      // Detaching first prevents multiple reflows.
      childWidgets.forEach(widget => {
          if(widget){
            // containerCanvasEl.append(widget.dom.detach());
            containerMarkupEl.append(widget.markup.detach());
            widget.dom.detach();
          }
      });

      if (stableReferenceNodeMarkup && stableReferenceNodeMarkup.length) {
          stableReferenceNodeMarkup.before(containerMarkupEl);
      } else {
          stableParentMarkup.append(containerMarkupEl);
      }

      this.dynamicComponentProvider.addComponentBasedOnSelector(
        stableReferenceNodeDom,
        containerCanvasEl[0].outerHTML.toString(),
        {},
        {
            transpile: true,
            addAfter: false, // Insert before the reference node
            parentEl: stableParentDom
        }
    ).then((el: HTMLElement) => {
          // The new container and its children are now live.
          const $newContainer = $(el);

          childWidgets.forEach(widget => {
            // Ensure we append into the container element's direct inner area.
            if(widget) $newContainer.append(widget.dom);
          });

          // Perform cleanup, like removing old list-item wrappers
          childWidgets.forEach(widget => {
              if (this.isListItem(widget?.oldParentContainer)) {
                  this.deleteListItem(widget?.oldParentContainer, widget?.oldParentMarkup);
              }
          });

              this.eventManager.emitOnCanvasUpdate({
                  ...containerInfo,
                  eventType: 'ADD',
                  widgetConfig: widgetConfig,
                  templateEl: containerMarkupEl
              });

              // Events for each widget move
              childWidgets.forEach((widget, index) => {
                  this.eventManager.emitOnCanvasUpdate({
                      widgetId: widget?.id,
                      parentId: containerInfo.widgetId,
                      prevParentId: containerInfo.parentId,
                      // The first widget has no reference, subsequent ones are placed after the previous one.
                      referenceId: index > 0 ? childWidgets[index - 1]?.id : undefined,
                      referencePos: index > 0 ? 'after' : 'before',
                      eventType: 'MOVE'
                  });
              });
          // }

          // Set focus/selection on the new container
          setTimeout(() => {
              const $activeWidgetEl = this.getWidgetToActivate($newContainer);
              if($activeWidgetEl){
                this.pageWsService.setWidgetPropertyChangedFlg(true);
                this.pageWsService.isWmEventTriggered = true;
              }
              $activeWidgetEl.click();
          }, 0, false);
      });
  }

  renderCrudGridTemplates(templateEl: JQuery<HTMLElement>, referenceEl: JQuery<HTMLElement>, obj: DroppedWidgetInfo, parentEl: any, preventSelectOnAdd: boolean | undefined, $activeWidgetEl: any) {
    let promises: any = [];
    templateEl.each((index, childEl) => {
      promises.push(this.dynamicComponentProvider.addComponentBasedOnSelector(referenceEl ? referenceEl[0] : null, (childEl.outerHTML).toString(), {}, {
        transpile: true,
        addAfter: obj.referencePos === 'after',
        parentEl: parentEl
      }))
    })
    // after rendering all the template elements, the last rendered element should be focused
    Promise.all(promises).then((el: any) => {
      setTimeout(() => {
        this.initializeAppearanceVariant(el);
        if (!preventSelectOnAdd) {
          $activeWidgetEl = this.getWidgetToActivate($(el[templateEl.children.length - 1]));
          $activeWidgetEl.click();
        }
      }, 0, false);
    })
  }

  bindVariableToEvent(createdVariableObj: any, widgetId: any) {
    let variableName = createdVariableObj.name,
      bindValue = "Variables." + variableName + ".invoke()";
    setTimeout(() => {
      runtimeEventManager.emit('set-markup-attr', { widgetId: widgetId, 'on-select': bindValue });
      runtimeEventManager.emit('set-event-attr', { widgetId: widgetId, 'onSelect': bindValue })
    });
    return;
  }

  bindDeleteFileVariableToEvent(createdVariableObj: any, widgetId: any) {
    let variableName = createdVariableObj.name,
      bindValue = "Variables." + variableName + ".invoke()";
    setTimeout(() => {
      runtimeEventManager.emit('set-markup-attr', { widgetId: widgetId, 'on-delete': bindValue });
      runtimeEventManager.emit('set-event-attr', { widgetId: widgetId, 'onDelete': bindValue })
    });
    return;
  }

  addFileUploadWidgetToCanvas(widgetId: any) {
    let serviceType, createNewVariable = true,
      CONSTANT_FILE_SERVICE = 'FileService',
      CONSTANT_FILE_OPERATION = 'uploadFile';
    /* if the file-upload-widget is already registered, add widget to canvas */
    serviceType = this.serviceFactory.getServiceObjectByName(CONSTANT_FILE_SERVICE).type;
    this.createServiceOperationVars(CONSTANT_FILE_SERVICE, serviceType, false, { 'files': 'bind:Widgets.' + widgetId.substring(3) + '.selectedFiles' }, CONSTANT_FILE_OPERATION, undefined, createNewVariable, widgetId, this.bindVariableToEvent, true);

    CONSTANT_FILE_OPERATION = 'deleteFile';
    this.createServiceOperationVars(CONSTANT_FILE_SERVICE, serviceType, false, { }, CONSTANT_FILE_OPERATION, undefined, createNewVariable, widgetId, this.bindDeleteFileVariableToEvent, true);
  }

  /*Function to create the service variables for the operations of the web/java service*/
  createServiceOperationVars(serviceName: any, serviceType: any, isReload: any, sampleParamValues: any, operation: any, isReImportRest: any, createNewVariable: any, widgetId: any, callBack: any, isUploadTypeVar: any) {
    let serviceMethods: any = [],
      methodCount = 0;

    (this.serviceFactory.getServiceOperations(serviceName) as Promise<any>).then((methods: any) => {
      /*loop through the operations to append them to the tree and get their params*/
      forEach(methods, (method: any, index: any) => {
        /*Create variable only for the selected operation otherwise create all variables*/
        if ((operation && operation === method.name) || !operation) {
          let variableDetails = {
            "service": serviceName,
            "operation": method.name,
            "owner": VARIABLE_CONSTANTS.OWNER.PAGE,
            "serviceType": serviceType,
            "sampleParamValues": sampleParamValues,
            "controller": method.controller,
            "operationId": method.operationId,
            "returnType": method.return && method.return.typeRef,
            "autoUpdate": isUploadTypeVar ? false : true
          };
          serviceMethods.push(method.name);

          if (VARIABLE_CONSTANTS.REST_SUPPORTED_SERVICES.indexOf(serviceType) !== -1) {
            methodCount += 1;
            let variableObjArray = this.variableService.filterByVariableKeys({ 'service': serviceName, 'operation': variableDetails.operation, 'category': 'wm.ServiceVariable' }),
              variableName,
              bindMapCollection: any = [],
              createdVariableObj;
            /*if variable already exists, just update it and save the variable*/
            if (variableObjArray.length > 0 && !createNewVariable) {
              variableObjArray.forEach((variable: any) => {
                /* insert sample param values if provided */
                bindMapCollection = variable.dataBinding;
                if (isObject(sampleParamValues)) {
                  Object.keys(sampleParamValues).forEach((key: any) => {
                    let result = findKey(bindMapCollection, { 'target': key });
                    if (!result) {
                      bindMapCollection.push({
                        "target": key,
                        "value": (sampleParamValues as any)[key],
                        "type": "java.lang.String"
                      });
                    }
                  });
                  forEach(bindMapCollection, (map: any, index: any) => {
                    if (!sampleParamValues.hasOwnProperty(map.target)) {
                      bindMapCollection.splice(index, 1);
                    }
                  });
                }
                this.variableService.updateVariable(variable.name, variable);
              });
            } else {
              /* During re-import don't create variable, user might have deleted it*/
              if (isReImportRest) {
                return;
              }
              /*if variable does not exist, create it and update the service info and save*/
              variableName = this.variableService.createServiceVariable(variableDetails, isReload, createNewVariable);
              createdVariableObj = this.variableService.getVariableByName(variableName);
              /*Open the variable dialog with first auto created variable as pre selected variable*/
              if (methodCount === 1) {
                /*[Shubham] The below code is commennted out for not showing the dialogue on each drag & drop of file widget
                textMessage = $rootScope.locale.MESSAGE_INFO_DEFAULT_VARIABLE;
                textMessage = textMessage + Utils.replace('<a href="${0}" target="_blank">(More info)</a>', [$rootScope.externalLinks.SERVICE_VARIABLES]);
                $scope.handleVariableNodeClick(variableName, undefined, {'text': textMessage, 'type': 'info'});*/
                /*Invoke the function to save the created variables.*/
                this.variableService.saveVariables(this.pageWsService.activePageName);
              }
              // fileService variable should be added to onSelect event
              callBack(createdVariableObj, widgetId);
            }
            if (methodCount === methods.length) {
              //removeDeprecatedOperationVariables(serviceName, serviceMethods);
              /*Invoke the function to save the created variables.*/
              this.variableService.saveVariables(this.pageWsService.activePageName);
            }
          } else {
            /*Invoke the function to create a variable.*/
            this.variableService.createServiceVariable(variableDetails, isReload, createNewVariable);
            if (index === methods.length - 1) {
              /*Invoke the function to save the created variables.*/
              this.variableService.saveVariables(this.pageWsService.activePageName);
            }
          }
        }
      });
    });
  }

  isListItem(parentContainer: JQuery<HTMLElement> | undefined) {
    return parentContainer?.attr("widgettype") === "wm-list-item" || parentContainer?.attr("widgettype") === "wm-nav-item";
  }

  deleteListItem(listItem: JQuery<HTMLElement> | undefined, markup: JQuery<HTMLElement> | undefined) {
    listItem?.remove();
    markup?.remove();
  }

  //get the active widget to be selected on canvas
  getWidgetToActivate($el: JQuery<HTMLElement>) {
    //If live grid is added to canvas, select the grid widget inside live grid
    if ($el.is('.app-livegrid')) {
      return $el.find('.app-grid');
    }

    //When a dialog is dropped, select the dialog instead of the enclosing view
    if ($el.is('.dialog-view')) {
      return $el.children().first();
    }

    return $el;
  }

  /*preparing element/widget markup using template for the widget(considering children as well)*/
  prepareElements(template: JQuery<HTMLElement>, processRoot?: boolean, actionPerformed?: any) {
    let widgetType, widgetId, widgetConfig;

    /*when the root element also needs to be processed,
     we add a div so the template.find(*) considers the root of the template as well*/
    if (processRoot) {
      template = $("<div></div>").append(template);
    }

    template.find('*').each((index: number, el: HTMLElement) => {
      let hasPageAttr, hasContentAttr, element: JQuery<HTMLElement>;

      element = $(el);

      if (element.closest('wm-table-column').length) {
        return;
      }

      widgetId = this.getWidgetId(element, actionPerformed);
      widgetType = this.getWidgetType(element);
      widgetConfig = widgetType ? this.WidgetProperties.getConfig(widgetType) : {};

      /*
       opposite to decorating the markup..
       add all the properties back that got removed in decorating phase
       */
      /*in case of html widget, its inner elements also get included in the markup but for them widgetconfig is null */
      if (widgetConfig) {
        element.attr({
          'widgetid': widgetId,
          'widget-id': widgetId,
          'widgettype': widgetType
        });
        // TODO: Harded the droptarget for prefab-container to make testim testcases work for both web and rn app.
        if (widgetType === 'wm-prefab-container') {
          const replaceId = 'wm_page_content1';
          element.attr({
            'widgetid': replaceId,
            'widget-id': replaceId
          });
        }

        /** The widgets inside the composite wrapper should not be draggable/copyable.
         *  The element has an attribute 'part' which says that it is inside a composite widget.
         **/
        if (widgetType === "wm-list" && this.projectService.isTemplateBundleType()) {
          element.attr('template', 'true');
        }

        if (!element.attr("name") && widgetType === 'wmx-component') {
          const componentName = element.attr('componentname');
          const index = widgetId.match(/[0-9]+$/)?.[0] || '';
          const customWidgetId = componentName ? componentName + index : widgetId.substring(4);
          element.attr('name', getUniqueWidgetName(this.markup, customWidgetId));
        }

        if (!element.attr("name")) {
          element.attr('name', getUniqueWidgetName(this.markup, widgetId.substring(3)));
        }
        // fix for [WMS-24129]: adding 'key' attribute for custom formfields
        if (widgetType === 'wm-form-field' && element.attr('type') === 'custom' && !element.attr('key')) {
            element.attr('key', element.attr('name') || getUniqueWidgetName(this.markup, widgetId.substring(3)));
        }
        if (!widgetConfig.isDeletable || element.attr('root') === "true") {
          element.attr('candelete', 'false');
        }
        if (widgetConfig.scaffold) {
          element.attr('scaffold', 'true');
        }

        if (widgetConfig.isDroppable && element.attr('root') !== "true") {
          element.attr('wm-droppable', 'true');
        }

        if (widgetConfig.isMovableInsideCanvas) {
          element.attr('movable-in-canvas', 'true');
        }

        if (widgetConfig.isTemplateBundled) {
          element.attr('is-template-bundled', 'true');
        }

        if (widgetConfig.isLayoutBundled) {
          element.attr('is-layout-bundled', 'true');
        }

        if (widgetConfig.isContainer) {
          element.attr('is-container', 'true');
        }

        if (!element.attr('tabindex')) {
          element.attr('tabindex', 0);
        }

        if (widgetConfig.isPageContainer) {
          /* support for deprecated page attribute for the page containers*/
          hasPageAttr = element[0].hasAttribute("page");
          hasContentAttr = element[0].hasAttribute("content");
          if (hasPageAttr) {
            if (!hasContentAttr) {
              element.attr("content", element[0].getAttribute("page"));
            }
            element.removeAttr("page");
          }
        }

        if (element.hasClass('dialog-view')) {
          element.attr('wm-droppable', 'false');
        }

        if (widgetType === 'wm-tabs') {
          if (element[0].hasAttribute("vertical")) {
            if (element.attr('vertical') === "true" && !element[0].hasAttribute("tabsposition")) {
              element.attr("tabsposition", "left");
            }
            element.removeAttr("vertical");
          }
        }
      }
    });
    return template;
  }

  /** We find out the widget type from the elements tagName*/
  getWidgetType(element: JQuery<HTMLElement>) {
    return element.prop('tagName').toLowerCase();
  }

  /**
   *   The widgetId and name should be based on the tag. It was earlier based on the widgetType, but now that we have composite widgets,
   *   the widgetType of composite widgets would be Text/Select etc. So we should consider the tag here for generating Id
   */
  getWidgetId(element: JQuery<HTMLElement>, actionPerformed?: string) {
    let tagName = element.prop('tagName').toLowerCase();

    // if the element is a prefab, generate the widgetid using the prefabName(ie, youtube1) instead of the tagname(ie, prefab1)
    if (tagName === 'wm-prefab') {
      tagName = 'wm-' + element.attr('prefabname');
    }
    else if(tagName === 'wm-custom-widget') {
      tagName = 'wm-' + element.attr('widgetname');
    }

    //toggle widget is a variant of checkbox.
    //if the type for the checkbox is set as toggle generate a different name.
    if (tagName === 'wm-checkbox' && element.attr('type') === 'toggle') {
      tagName = 'wm-toggle';
    }else if(tagName === 'wm-checkbox' && element.attr('type') === 'button'){
      tagName = "wm-iconbutton-toggle"
    }

    // on paste of a widget, always generate uniqueID
    if (actionPerformed === 'paste') {
      return this.markupManager.getUniqueIdByType(tagName);
    }

    return element.attr('widget-id') || element.attr('widgetid') || this.markupManager.getUniqueIdByType(tagName);
  }

  /**
   * If we add any attribute on widgets based on the widget property such as widgetId, name
   * Then these attributes are evaluated and replaced with their values here
   * @param elem,
   */
  evaluateElemAttr(elem: HTMLElement) {
    let attrVal, attrObj = {};
    elem.getAttributeNames().forEach(function (attr) {
      attrVal = elem.getAttribute(attr);
      set(attrObj, attr, attrVal ? attrVal : undefined);
    });

    elem.getAttributeNames().forEach(function (attr) {
      attrVal = elem.getAttribute(attr);
      if (attrVal?.startsWith('$wm-expr(')) {
        attrVal = attrVal.substring(9, attrVal.length - 1);
        let evaluatedData = getEvaluatedData(attrObj, { expression: attrVal }, {});
        elem.setAttribute(attr, evaluatedData)
      }
    });
  }

  /**
   * this method applies the default properties to the template i.e, before compiling an element.
   * hence don't need to trigger property-change event.
   */
  applyWidgetDefaults(templateEl: JQuery<HTMLElement>, widgetType: string, widgetDefaults: { [key: string]: string } | string) {
    if (widgetDefaults) {

      /*Utils.getValidJSON is used because widgetDefaults is passed as a string containing (key,value) pairs of attributes*/
      let _widgetDefaults: { [key: string]: string } = getValidJSON(widgetDefaults) || {};

      forEach(_widgetDefaults, (value, key) => {
        /* widgetdefaults can now be added for inner elements as well,
         * the key value pair is now type of the inner element and the key value pair to be added as attribute */
        if (isObject(value)) {
          let newTemplate;
          if (templateEl.is('[widgettype="' + key + '"]')) {
            newTemplate = templateEl;
          } else {
            newTemplate = templateEl.find('[widgettype="' + key + '"]');
          }

          $(newTemplate).attr(value);
        } else {
          templateEl.attr(key, value);
        }
      });
    }
  }

  performActionBasedOnKey(key: any, event?: any) {
    // disabling the keyboard events based on rbac permissions
    if(!get(this.preferences, 'project.pages.edit')) {
      return;
    }
    switch (key) {
      case 'ESC':
        event?.stopPropagation();
        event?.preventDefault();
        this.selectParentWidget();
        break;
      case 'DELETE':
        event?.stopPropagation();
        event?.preventDefault();
        if(this.canvasDomService.getSelectionCount() === 1){
          this.deleteWidgetProxy({ element: this.activeWidget, widgetId: this.activeWidget?.attr('widgetid') });
        } else {
          this.notificationService.show('info', '', 'Delete is not supported for Multi-Selection mode. Select a single widget to delete.');
        }
        break;
      case 'COPY':
        event?.stopPropagation();
        event?.preventDefault();
        if(this.canvasDomService.getSelectionCount() === 1){
          this.action = "copy";
          this.copyWidget();
        } else {
          this.notificationService.show('info', '', 'Copy is not supported for Multi-Selection mode. Select a single widget to copy.');
        }
        break;
      case "PASTE":
        event?.stopPropagation();
        event?.preventDefault();
        this.pasteWidget();
        break;
      case "CUT":
        event?.stopPropagation();
        event?.preventDefault();
        if(this.canvasDomService.getSelectionCount() === 1){
          this.action = "cut";
          if (this.copyWidget()) {
            this.deleteWidgetProxy({ element: this.activeWidget, widgetId: this.activeWidget?.attr('widgetid') });
          }
        } else {
          this.notificationService.show('info', '', 'Cut is not supported for Multi-Selection mode. Select a single widget to cut.');
        }
        break;
      case "DUPLICATE":
        this.action = "copy";
        this.copyWidget();
        this.pasteWidget();
        break;
      case "LEFT-ARROW":
        this.selectSiblingWidget('prev');
        break;
      case "RIGHT-ARROW":
        this.selectSiblingWidget('next');
        break;
      case "CTRL-LEFT-ARROW":
      case "CTRL-UP-ARROW":
        this.swapWidget('prev', event);
        break;
      case "CTRL-RIGHT-ARROW":
      case "CTRL-DOWN-ARROW":
        this.swapWidget('next', event);
        break;
    }
  }

  registerCanvasEvents() {
    this.domElement.on('click', (event: ClickEvent) => {

      // shift-click/ignore logic — helper returns true when we should return early
      const shouldReturnEarly = this.processShiftClick(event);
      if (this.projectService.isPrism() && shouldReturnEarly) { // Handle multiselect autolayout only for prism projects
        return;
      }

      event.stopPropagation();
      event.preventDefault();
      this.eventManager.emitCanvasDomClick('');
      this.handleClick(this.getElements(event));
      // Added check for tabs in studioMode: As focus on tabpane is causing issues during swipe transitions.
      if (!event.originalEvent && event.target.getAttribute('widgettype') !== 'wm-tabpane') {
        // For few widgets like label, panel , document.activeElement is still on the canvas even after invoking the focus.
        // Hence assigning the tabIndex to -1 to set the document.activeElement and recognize the keydown event or any event on it.
        this.activeWidget?.attr('tabIndex', '-1');
        this.activeWidget?.focus();
      }
    });

    this.domElement.on('keydown', (event: KeyDownEvent) => {
      let key = getActionFromKey(event);
      this.performActionBasedOnKey(key, event);
    });
  }

  /*function that handles the paste event of a copied widget. this function internally uses addWidget() method*/
  pasteWidget() {
    let parent: any = this.markupManager.getActiveCanvasEl(),
      parentId = parent.attr('widgetid'),
      isDroppable = parent.attr("wm-droppable"),
      _activeWidId,
      copiedWidgetConfig: any,
      widgetType,
      clonedCopy;

    if (!isDroppable || isDroppable === false || isDroppable === "false") {
      parent = parent.parent().closest("[widgetid]");
      parentId = parent.attr('widgetid');
    }

    if (!parentId) {
      return;
    }

    if (this.copiedMarkup) {
      /** Clone the element that was copied, remove the name and widgetid from the element
       * and pass this element to add widget, which will not create the markup again
       * This way the attributes for the element need not be copied*/
      clonedCopy = this.copiedMarkup.clone();

      /**
       * If the widget is cut pasted, use the same markup to paste the widget.
       * */
      if (this.action === "copy" || this.action === "paste") {
        clonedCopy.removeAttr('name widgetid widget-id key');
        clonedCopy.find('*').removeAttr('name widgetid widget-id key');
      }
      this.action = "paste";
      widgetType = this.getWidgetType(clonedCopy);
      copiedWidgetConfig = {
        "widgetType": widgetType,
        "parentId": parent.attr('widgetid'),
        "templateEl": clonedCopy,
        "action": this.action
      };

      _activeWidId = this.activeWidget?.attr('widgetid');

      if (this.copiedMarkup.attr('widgetid') === _activeWidId) {
        copiedWidgetConfig['referenceId'] = _activeWidId;
        copiedWidgetConfig['referencePos'] = 'after';
      }
      this.wmCommand.execute(new CommandController(this.markupManager), this, 'addWidget', copiedWidgetConfig);
      this.pageWsService.startAutoSave();
    }
  }

  copyWidget() {
    let canBeCopiedEl: JQuery<HTMLElement> | null | undefined = this.markupManager.getActiveMarkupEl(),
      widgetConfig,
      errorMsgKey,
      widgetType;

    if (canBeCopiedEl) {
      widgetType = this.getWidgetType(canBeCopiedEl);
    }

    /*copy the element only if the element config canBeCopied property is true*/
    widgetConfig = widgetType ? this.WidgetProperties.getConfig(widgetType) : {};
    if (widgetConfig.canBeCopied) {
      this.copiedMarkup = canBeCopiedEl;
      runtimeEventManager.emit('set-copied-markup', this.copiedMarkup);
    } else {
      errorMsgKey = (this.action === "copy") ? "MESSAGE_ERROR_COPY_WIDGET_NOT_ALLOWED" : "MESSAGE_ERROR_CUT_WIDGET_NOT_ALLOWED";
      const data = {
        type: 'error',
        title: this.locale["MESSAGE_ERROR_TITLE"],
        desc: this.locale[errorMsgKey]
      }
      runtimeEventManager.emit('show-toaster', data);
      return false;
    }
    return true;
  }

  performAction(data: any) {
    switch (data.key) {
      case "UNDO":
        data.event.stopPropagation();
        data.event.preventDefault();
        runtimeEventManager.emit('on-undo-shortcut');

        break;
      case "REDO":
        data.event.stopPropagation();
        data.event.preventDefault();
        runtimeEventManager.emit('on-redo-shortcut');
        break;
    }
  }


 async handleClick(details: selectedWidgetInfo,) {
   const pageSubCategory = this.pageWsService.pageSubCategory;
   const excludedCategories = ['BUILD', 'STRUCTURE'];
    let widgetId = details.widgetId;
    /** Do not call selectWidget when there is no widgetId*/
    if (!widgetId) {
      return;
    }

    if(details.element && details.element.attr('dataset.bind')) {
     await this.getDataTypesOfWidget(details);
    }
if( details.event.originalEvent && pageSubCategory && !excludedCategories.includes(pageSubCategory)){
  this.pageWsService.pageSubCategory = 'BUILD'
  updateUrlParams([{ key: "m", value: "components" }], this.router, this.route);
}
    this.selectWidget({ widgetId: widgetId , isTrusted: details.event.originalEvent?.isTrusted }, true, true);
  }

 async getDataTypesOfWidget(widgetDetails: any) {
    const bindDataSet = widgetDetails.element[0].widget.binddataset;
    if(!bindDataSet) {
      return;
    }

    if (bindDataSet.startsWith('bind:Variables')) {
      const variableName = bindDataSet.split('.')[1];
      await this.getDataTypesByVariableName(variableName, widgetDetails);
    } else if (bindDataSet.startsWith('bind:Widgets')) {
      const widgetList = byType('form-widgets');
      let widgetName = bindDataSet.split('.')[1];
      let nextWidget = widgetList[widgetName];

      // Loop to process nested widgets
      while (nextWidget && nextWidget.binddataset?.startsWith('bind:Widgets')) {
        widgetName = nextWidget.binddataset.split('.')[1];
        nextWidget = widgetList[widgetName];
      }

      // Process the final widget or variable
      if (nextWidget && nextWidget.binddataset?.startsWith('bind:Variables')) {
        const variableName = nextWidget.binddataset.split('.')[1];
        await this.getDataTypesByVariableName(variableName, widgetDetails);
      }
    }
  }

  findVariableInVariableCollection(variableName: string) {
    for (const collection of Object.values(this.variableService.variableCollection)) {
      const variable = (collection as any)[variableName];
      if (variable) {
        return variable;
      }
    }
  }

  async getDataTypesByVariableName(variableName: any, widgetDetails: any) {
    const variable: any = this.findVariableInVariableCollection(variableName);
    if (variable) {
      const service = variable.liveSource || variable.service;
      await this.typeService.getDataTypesByService(service, true);
    }
  }


  getElements(event: ClickEvent): selectedWidgetInfo {
    let target = event.target;
    if (target.tagName.startsWith('WM-DYNAMIC-COMPONENT')) {
      target = target.firstChild;
    }
    let closestEl = target && $(target).closest('[widget-id]');
    return {
      event: event,
      element: closestEl,
      widgetId: closestEl?.attr('widget-id'),
      widgetType: closestEl?.attr('widgettype')
    };
  }

  switchTemplate(data: any) {
    // for widget template replacing the markup of active widget
    if (this.activeWidget) {
      data.markup.attr('name', this.activeWidget.attr('name'))
    }
    this.updateMarkup(data.markup, data.nodeName);
  }

  switchLayout(data: any) {
    let activeWidgetMarkup = this.markupManager.getActiveMarkupEl()?.clone();

    this.setWidgetProperties(activeWidgetMarkup, data);
    this.updateMarkup(activeWidgetMarkup, data.nodeName);
  }

  switchPageLayout(layoutObj: any) {
    let parent = this.domElement.find('div[wmpage]');
    const { markup } = this.findElementsByReference(parent.attr('widgetid'));
    let pageContent = this.pageLayoutService.switchLayout(this.markup, layoutObj);
    this.markupManager.resetIdMap();
    this.dynamicComponentProvider.addComponentBasedOnSelector(null, this.prepareMarkup(pageContent.html()), {}, {
      transpile: true
    }).then((el: HTMLElement) => {
      parent.replaceWith($(el));
      if (markup) {
        // @ts-ignore
        markup[0].innerHTML = pageContent[0].firstChild?.innerHTML;
        this.eventManager.emitOnWidgetPropChanged(true);
        this.eventManager.emitUpdatePageStructure();
        this.eventManager.emitUpdateViewSelector();
      }
    });
  }

  setWidgetProperties($templateEle: JQuery<HTMLElement> | undefined, data: any) {
    let layoutInfo = data.layoutInfo;
    if (!layoutInfo) return;

    // setting properties on the widget root element.
    if (layoutInfo.properties) {
      $templateEle?.attr(data.layoutInfo.properties);
    }

    let childNodeProperties = layoutInfo.childNodeProperties;
    if (!childNodeProperties) return;

    forEach(childNodeProperties, (childNodeProperties, childNodeName) => {
      let $childNodes = $templateEle?.find(childNodeName);
      if (isArray(childNodeProperties)) {
        $childNodes?.each((index, childNode) => {
          // in case actual child nodes are more than child nodes supplied by config json, restart the index
          let idx = index < childNodeProperties.length ? index : index % childNodeProperties.length;
          $(childNode).attr(childNodeProperties[idx]);
        });
      } else {
        $childNodes?.attr(childNodeProperties);
      }
    });
  }

  updateMarkup(markup: JQuery<HTMLElement> | undefined, nodeName: string) {
    let isTemplateBundleProject = this.appRef.isTemplateBundleType,
      isPrefabProject = this.appRef.isPrefabType,
      pageMarkup = this.markupManager.getMarkup(),
      partialContent = pageMarkup?.find('wm-partial:first'),
      isPartialParent = !!partialContent?.length,
      page = isPartialParent ? partialContent : pageMarkup?.find('wm-page:first'),
      pageContent = isPartialParent ? page : isTemplateBundleProject ? pageMarkup?.find('wm-template:first') : isPrefabProject ? pageMarkup?.find('wm-prefab-container:first') : page,
      parents = this.findElementsByReference(pageContent?.attr('widgetid')),
      parentEl = parents.dom,
      parentElMarkup = parents.markup,
      templateEl: JQuery<HTMLElement>, previousEl, hasIScope, clonedTemplateEl;
    if (markup) {
      templateEl = $(markup);
      this.prepareElements(templateEl, true);
      clonedTemplateEl = templateEl.clone();

      let activeCanvasEl = this.markupManager.getActiveCanvasEl();
      let previousElMarkup = this.markupManager.getActiveMarkupEl();

      if (nodeName && activeCanvasEl) {
        previousEl = activeCanvasEl.find(nodeName);
        hasIScope = get(previousEl[0], 'widget');
      }

      if (!hasIScope) {
        previousEl = activeCanvasEl && get(activeCanvasEl[0], 'widget.$element');
      } else {
        previousElMarkup = previousElMarkup?.find(nodeName);
      }

      if (previousEl) {
        parentEl?.find(previousEl).replaceWith(templateEl);
      }
      this.dynamicComponentProvider.addComponentBasedOnSelector(null, (templateEl[0].outerHTML).toString(), {}, {
        transpile: true
      }).then((el: HTMLElement) => {
        parentEl?.find(templateEl).replaceWith($(el));
        setTimeout(() => {
          this.setActiveWidget(templateEl.attr('widgetid'));
        });
      });

      if (previousElMarkup) {
        parentElMarkup?.find(previousElMarkup).replaceWith(clonedTemplateEl);
      }

      if (!hasIScope) {
        this.activeWidget = null;
      }
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.activeWidget = null;
    this.registerDestroyListener.unsubscribe();
    this.domElement.off('click keydown');
    runtimeEventManager.off('widget-name');
  }

  renderCanvasContent(markup: string, widgetId: string, widgetName: string) {
    return this.dynamicComponentProvider.getComponentFactoryRef(
      widgetId,
      markup,
      {
        noCache: true,
        transpile: true
      });
  }

  /**
   * Initializes the appearance and variant options for a widget and all its child widgets recursively.
   * Uses a Set to track visited elements and prevent redundant processing or infinite loops.
   *
   * @param el - The root widget HTMLElement to initialize.
   * @param visited - A Set of already-processed elements (used for recursion safety).
   */
  initializeAppearanceVariant(el: HTMLElement, visited = new Set<HTMLElement>()): void {
    // Prevent processing the same element more than once (avoids infinite loops in case of cycles)
    if (visited.has(el)) return;
    visited.add(el);

    // Get the widget instance from the element
    const widgetInstance = (el as any)?.widget;
    if (!widgetInstance || !widgetInstance.widgetProps?.variant) return;

    if (widgetInstance && widgetInstance.widgetProps?.variant) {
      const fileName = widgetInstance.tokenFileName;

      if (['wm-button', 'wm-tile'].includes(widgetInstance.widgetType)) {
        widgetInstance.class = '';
      }

      const widgetId = widgetInstance.$attrs?.get?.('widgetid');

      // Extract the appearance/variant map and update widget properties accordingly
      widgetInstance.widgetStylingService.extractAppearanceVariantMap(fileName).then((appearanceMap: any) => {
        const variantOptions = widgetInstance.widgetProps?.variant?.options || [];
        // No variants available
        if (!variantOptions.length) {
          widgetInstance.variant = '';
          widgetInstance.widgetProps.variant.value = '';
          runtimeEventManager.emit('set-markup-attr', { widgetId, variant: '' });
          return;
        }

        const validOptions = variantOptions.filter((variant : any) => !variant.custom)
        const selectedVariant = widgetInstance.variant || validOptions[0].value

        widgetInstance.syncAppearanceVariantClasses(selectedVariant, "");
        widgetInstance.variant = selectedVariant;
        widgetInstance.widgetProps.variant.value = selectedVariant;

        runtimeEventManager.emit('set-markup-attr', { widgetId, variant: selectedVariant });
        runtimeEventManager.emit('updateShadowProperty');
      });
    }

    // Recursively initialize for child widgets
    // Find all descendants with a widgetid attribute (potential widgets)
    const childElements = (el as HTMLElement).querySelectorAll('[widgetid]');
    childElements.forEach((childEl: Element) => {
      const childWidget = (childEl as any).widget;
      if (childWidget && childWidget.widgetProps?.variant) {
        this.initializeAppearanceVariant(childEl as HTMLElement, visited);
      }
    });
  }

  /**
   * Adds btn-filled class to form action buttons for prism projects
   * @param templateEl - The template element to modify
   */
  private addBtnFilledToFormActionButtons(templateEl: JQuery<HTMLElement>): void {
    // Finding all form action buttons and add btn-filled class
    templateEl.find('wm-form-action').each((index, element) => {
      const $element = $(element);
      let currentClass = $element.attr('class') || '';
      // Replace btn-success with btn-primary
      currentClass = currentClass.replace('btn-success', 'btn-primary');

      // Add btn-filled to existing classes, avoiding duplicates
      if (!currentClass.includes('btn-filled')) {
        currentClass = currentClass + ' btn-filled';
      }

      // Update the class attribute
      $element.attr('class', currentClass);
    });
  }

    /**
   * Adds btn-filled class to dialog action buttons and btn-text for dialog close for prism projects
   * @param templateEl - The template element to modify
   */
  private addBtnClassesToDialogButtons(templateEl: JQuery<HTMLElement>): void {
    // Finding all dialog action buttons and add btn-filled class
    templateEl.find('wm-dialogactions').children()?.each((index, element) => {
      const $element = $(element);
      let currentClass = $element.attr('class') || '';

      // Add btn-filled to existing classes, avoiding duplicates
      if (!currentClass.includes('btn-filled')) {
        currentClass = currentClass + ' btn-filled';
      }

      // Update the class attribute
      $element.attr('class', currentClass);
    });

    // Set btn-text class on dialog close-btn
    setTimeout(() => {
      let canvasEl = getCanvasElement();
      let closeBtnEl = $(canvasEl).find('.app-dialog-close.close');
      if (closeBtnEl) {
        let currentClass = $(closeBtnEl).attr('class') || '';

        currentClass = currentClass + ' btn-text'
        $(closeBtnEl).attr('class', currentClass);
      }
    }, 100);
  }

  /**
   * Handle shift+click special cases and decide whether registerCanvasEvents should return early.
   * Returns true when the caller should return early (i.e. NOT do the normal selectWidget/handleClick flow).
   */
  private processShiftClick(event: ClickEvent): boolean {
    // if the canvas dom service says this click should be ignored, return early
    if (this.canvasDomService.shouldIgnoreClick(event)) {
      return true;
    }

    // only special-case shift+click
    if (!event.shiftKey) {
      return false;
    }

    const details = this.getElements(event);
    const clickedWidgetId = details?.widgetId;

    // click wasn't on a widget -> let CanvasDomService handle the click (early return)
    if (!clickedWidgetId) {
      return true;
    }

    const selCount = this.canvasDomService.getSelectionCount();
    const isSelected = this.canvasDomService.isWidgetSelected(clickedWidgetId);

    // special case: clicked a selected widget while exactly 2 widgets are selected
    if (isSelected && selCount === 2) {
      const remainingId = this.canvasDomService.getRemainingWidgetIdAfterDeselection(clickedWidgetId);
      if (remainingId) {
        const els = this.findElementsByReference(remainingId);
        const info: any = {
          event,
          element: els.dom,
          widgetId: remainingId,
          widgetType: els.dom?.attr('widgettype')
        };
        // Call handleClick for the remaining widget
        this.handleClick(info);
      }
      return true;
    }

    // If there is any existing selection (but not the above 2-case), we don't call handleClick here.
    // Let CanvasDomService handle the toggle silently.
    if (selCount > 0) {
      return true;
    }

    // No existing selection -> allow normal selection flow.
    return false;
  }
}
